/**
 * NAV-4.2 — is this a MOBILE BROWSER, as distinct from a narrow window?
 *
 * WHY THIS IS DEVICE DETECTION AND NOT A MEDIA QUERY, which is the whole reason it exists:
 *
 * The one thing this answers is whether to trust `100vh`. Mobile browsers collapse and expand their
 * address bar, so `100vh` measures something the user cannot actually see, and the app compensates with a
 * `--vh` custom property recomputed on resize. That is a property of the BROWSER, not of the viewport
 * width — an iPad in landscape is 1024px wide and still has the collapsing-chrome problem, so a
 * `max-width: 767px` query would answer "not mobile" and reintroduce the bug on every tablet.
 *
 * ⚠️ SO DO NOT REPLACE THIS WITH `useMediaQuery`. Layout questions ("should the sidebar overlay the
 * content?") ARE viewport questions and belong in a `md:` breakpoint, where CSS answers them and nothing
 * can go stale. This function answers only the `100vh`-is-lying question.
 *
 * EXTRACTED FROM MainLayout so the scoring rule is testable at all: `vitest.config.ts` includes only
 * `src/**\/*.test.ts` under `environment: 'node'` with no jsdom, so nothing inside a component is
 * reachable by a test.
 */

/** The raw signals, injected so the rule can be tested without a browser. */
export interface MobileSignals {
  /** `navigator.maxTouchPoints`. Absent/0 on a mouse-driven machine. */
  maxTouchPoints: number;
  userAgent: string;
  devicePixelRatio: number;
  /** `'orientation' in window` — present on mobile browsers. */
  hasOrientationApi: boolean;
  /** `window.screen.width` / `.height` — the DEVICE screen, not the window. */
  screenWidth: number;
  screenHeight: number;
}

const MOBILE_UA =
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;

/** Two of four indicators. Deliberately a vote, not an `&&`: any single signal is spoofable or absent. */
export const MOBILE_INDICATOR_THRESHOLD = 2;

/**
 * The four indicators, exposed so a test can assert WHICH ones fired rather than only the verdict — a
 * 2-of-4 rule that reaches the right answer for the wrong reasons is a rule that will drift.
 */
export function mobileIndicators(s: MobileSignals): {
  hasTouch: boolean;
  hasMobileUA: boolean;
  hasHighDprWithOrientation: boolean;
  hasSmallScreen: boolean;
} {
  return {
    hasTouch: s.maxTouchPoints > 0,
    hasMobileUA: MOBILE_UA.test(s.userAgent),
    // Paired on purpose: a high DPR alone is every modern laptop. With an orientation API it is a phone.
    hasHighDprWithOrientation: s.devicePixelRatio > 1.5 && s.hasOrientationApi,
    /*
     * ⚠️ AN `OR`, AND THAT IS WHAT MAKES THIS ROTATION-STABLE. `screen.width`/`screen.height` swap on some
     * browsers when the device rotates, so `width <= 768` alone would flip on a rotate. Testing EITHER
     * dimension gives the same answer in both orientations — a phone has one small dimension whichever way
     * up it is, and a desktop monitor has neither.
     */
    hasSmallScreen: s.screenWidth <= 768 || s.screenHeight <= 768,
  };
}

/** True when at least `MOBILE_INDICATOR_THRESHOLD` indicators fire. Survives "Request Desktop Site". */
export function isMobileFromSignals(s: MobileSignals): boolean {
  const i = mobileIndicators(s);
  const votes = [
    i.hasTouch,
    i.hasMobileUA,
    i.hasHighDprWithOrientation,
    i.hasSmallScreen,
  ].filter(Boolean).length;
  return votes >= MOBILE_INDICATOR_THRESHOLD;
}

/**
 * Read the signals from the live browser.
 *
 * Guarded for a non-browser context so importing this module never throws under `environment: 'node'`.
 */
export function readMobileSignals(): MobileSignals {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      maxTouchPoints: 0,
      userAgent: '',
      devicePixelRatio: 1,
      hasOrientationApi: false,
      screenWidth: 1920,
      screenHeight: 1080,
    };
  }
  return {
    maxTouchPoints: typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints : 0,
    userAgent: navigator.userAgent ?? '',
    devicePixelRatio: window.devicePixelRatio ?? 1,
    hasOrientationApi: 'orientation' in window,
    screenWidth: window.screen?.width ?? 1920,
    screenHeight: window.screen?.height ?? 1080,
  };
}

/** Convenience for the live browser. */
export function isMobileDevice(): boolean {
  return isMobileFromSignals(readMobileSignals());
}
